/* wishlist.js – Binder + List view (mobile + performance)
   - Binder view: 3×3 spreads
   - List view: binder UI hidden; responsive grid of product cards
   - View toggle persisted in localStorage
   - Touch-first overlays (mobile) + hover/focus (desktop)
   - Swipe left/right (binder view only)
   - Pager dots support (binder view, if .wl-pager exists)
   - Lazy/async image loading (+ optional prefetch of next spread)
   - Capped-concurrency hydration + session + LS cache (6h TTL)
   - Batched rerenders; ResizeObserver to keep fit correct on desktop
   - Drag & drop reordering in Binder (right page)
   - List view shows price next to Add to Cart + Remove
   - List view: hover pop-out (image + card) and slide-in price animation
*/
(function () {
  /* ========================== Constants & Utils ========================== */
  const KEY = 'wl_items_v1';
  const BTN_SELECTOR = '[data-wishlist-btn], .wl-btn[data-wl-handle], .wl-btn[data-product-handle]';

  const PAGE_SIZE_PER_SIDE = 9; // 3×3
  const ITEMS_PER_PAGE = PAGE_SIZE_PER_SIDE;
  const MQ_NARROW = '(max-width: 768px)';

  // Hydration / cache
  const HYDRATE_MAX_CONCURRENCY = 4;
  const PRODUCT_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours
  const LS_PRODUCT_CACHE = 'wl_product_cache_v1';

  // View toggle
  const LS_VIEW_MODE = 'wl_view_mode';        // 'binder' | 'list'
  const VIEW_BINDER = 'binder';
  const VIEW_LIST   = 'list';

  // In-memory product cache (session)
  const productCache = new Map();

  function getStore() {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; }
  }
  function setStore(list) {
    try { localStorage.setItem(KEY, JSON.stringify(list)); } catch {}
  }

  function getHandleAndImg(btn) {
    const handle =
      btn.getAttribute('data-wl-handle') ||
      btn.getAttribute('data-product-handle') ||
      '';
    const img =
      btn.getAttribute('data-wl-img') ||
      btn.getAttribute('data-product-img') ||
      '';
    return { handle, img };
  }

  function isSavedHandle(handle) {
    if (!handle) return false;
    const list = getStore();
    return list.some(x => (typeof x === 'string' ? x : x.handle) === handle);
  }

  function toggleSavedForBtn(btn) {
    const { handle, img } = getHandleAndImg(btn);
    if (!handle) return false;
    const list = getStore();
    const idx = list.findIndex(x => (typeof x === 'string' ? x : x.handle) === handle);
    if (idx >= 0) list.splice(idx, 1);
    else list.push({ handle, img });
    setStore(list);
    return list.some(x => (typeof x === 'string' ? x : x.handle) === handle);
  }

  function reflectState(btn, saved) {
    btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    btn.classList.toggle('is-active', saved);
  }

  function initButtons(scope = document) {
    scope.querySelectorAll(BTN_SELECTOR).forEach(btn => {
      const { handle } = getHandleAndImg(btn);
      if (!handle) return;
      reflectState(btn, isSavedHandle(handle));
    });
  }

  // Beat stretched links for any wishlist buttons globally
  document.addEventListener('click', (e) => {
    const btn = e.target.closest(BTN_SELECTOR);
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const { handle } = getHandleAndImg(btn);
    if (!handle) return;
    const saved = toggleSavedForBtn(btn);
    reflectState(btn, saved);
    document.dispatchEvent(new CustomEvent('wishlist:changed', { detail: { handle, saved } }));
  }, true);

  document.addEventListener('DOMContentLoaded', () => initButtons());
  document.addEventListener('shopify:section:load', (evt) => initButtons(evt.target));

  /* ========================== Product fetching =========================== */
  function readProductLS() {
    try { return JSON.parse(localStorage.getItem(LS_PRODUCT_CACHE)) || {}; } catch { return {}; }
  }
  function writeProductLS(obj) {
    try { localStorage.setItem(LS_PRODUCT_CACHE, JSON.stringify(obj)); } catch {}
  }
  const productLS = readProductLS();

  function getCachedProduct(handle) {
    const c = productLS[handle];
    if (!c) return null;
    if ((Date.now() - c.ts) > PRODUCT_TTL_MS) return null;
    return c.data;
  }
  function setCachedProduct(handle, data) {
    productLS[handle] = { data, ts: Date.now() };
    writeProductLS(productLS);
  }

  async function fetchProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);

    const lsData = getCachedProduct(handle);
    if (lsData) {
      productCache.set(handle, lsData);
      return lsData;
    }

    try {
      const res = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('404');
      const data = await res.json();
      productCache.set(handle, data);
      setCachedProduct(handle, data);
      return data;
    } catch {
      return null;
    }
  }

  function normaliseSaved(entry) {
    return typeof entry === 'string'
      ? { handle: entry, img: '' }
      : { handle: entry.handle, img: entry.img || '' };
  }

  // Concurrency-capped hydration
  async function hydrate(list) {
    const items = list.map(normaliseSaved).filter(x => x.handle);
    const out = new Array(items.length);
    let i = 0;

    async function worker() {
      while (i < items.length) {
        const idx = i++;
        const { handle, img } = items[idx];
        const data = await fetchProduct(handle);
        if (!data) { out[idx] = null; continue; }

        const v = (data.variants || []).find(v => v.available) || (data.variants || [])[0] || null;
        const priceCents = (v && typeof v.price === 'number') ? v.price : (typeof data.price === 'number' ? data.price : null);

        out[idx] = {
          handle,
          title: data.title,
          url: `/products/${handle}`,
          price: priceCents,                                   // cents (may be null)
          image: (data.images && data.images[0]) || img || '', // FRONT
          backImage: (data.images && data.images[1]) || '',    // BACK
          variantId: v ? v.id : null
        };
      }
    }

    const workers = Array.from({ length: Math.min(HYDRATE_MAX_CONCURRENCY, items.length) }, worker);
    await Promise.all(workers);
    return out.filter(Boolean);
  }

  function formatMoney(cents) {
    if (cents === null || cents === undefined) return '';
    try { return Shopify.formatMoney(cents); } catch {}
    try {
      return (cents / 100).toLocaleString(undefined, {
        style: 'currency',
        currency: '{{ shop.currency }}'
      });
    } catch {
      return `${(cents / 100).toFixed(2)}`;
    }
  }

  /* ============================== Cart API ============================== */
  async function addToCart(variantId, qty = 1) {
    if (!variantId) throw new Error('Missing variant');
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty })
    });
    if (!res.ok) throw new Error('Add to cart failed');
    return res.json();
  }

  /* ============================ HTML helpers ============================ */
  // Binder front card
  function cardFrontHTML(p) {
    const front = p.image || '';
    const hasPrice = p.price !== null && p.price !== undefined;
    const priceHtml = hasPrice ? `<span class="wl-card__hover-price" style="
      background: rgba(0,0,0,.75); color:#fff;
      font-weight: 600; font-size: .85rem; padding: .35rem .6rem;
      border-radius: 999px; white-space: nowrap;">${formatMoney(p.price)}</span>` : '';

    const addBtn = p.variantId
      ? `<button type="button" class="wl-card__addtocart" data-variant-id="${p.variantId}" data-handle="${p.handle}" style="
          pointer-events:auto;border:0;border-radius:999px;padding:.5rem .9rem;
          background:#1a7f37;color:#fff;font-weight:700;font-size:.9rem;cursor:pointer;
          box-shadow:0 2px 8px rgba(26,127,55,.35);">Add to Cart</button>`
      : '';

    return `
      <article class="wl-card wl-front" data-handle="${p.handle}" draggable="true" style="position:relative; overflow:hidden;">
        <a href="${p.url}" class="wl-card__image-link" aria-label="${p.title}" tabindex="-1" style="
             position:relative; display:block; width:100%;
             aspect-ratio:63/88; background:#fff; overflow:hidden;">
          ${front ? `<img class="wl-card__img"
               src="${front}"
               alt="${p.title}" loading="lazy" decoding="async" style="
               display:block; width:100%; height:100%; object-fit:cover; object-position:center;">` : ''}
        </a>
        <div class="wl-card__overlay" data-overlay style="
          position:absolute; left:0; right:0; bottom:6px;
          display:flex; gap:8px; justify-content:center; align-items:center;
          padding:0 8px; pointer-events:none; opacity:0; transform:translateY(6px);
          transition:opacity .18s ease, transform .18s ease; z-index:2;">
          ${priceHtml}${addBtn}
        </div>
        <button type="button" class="wl-card__remove" data-wl-remove data-wl-handle="${p.handle}"
                aria-label="Remove ${p.title}" style="
                position:absolute;top:6px;right:6px;line-height:1;border:none;
                background:rgba(0,0,0,.55);color:#fff;border-radius:999px;
                width:36px;height:36px;font-size:22px;display:flex;align-items:center;justify-content:center;">×</button>
      </article>
    `;
  }

  // Binder back card
  function cardBackHTML(p, fallbackBackImg = '') {
    const back = p.backImage || fallbackBackImg || p.image || '';
    return `
      <article class="wl-card wl-back" data-handle="${p.handle}" style="position:relative;">
        <div class="wl-card__image-link" style="display:block; width:100%; aspect-ratio:63/88; overflow:hidden;">
          ${back ? `<img class="wl-card__img"
               src="${back}"
               alt="" loading="lazy" decoding="async" style="
               display:block; width:100%; height:100%; object-fit:cover; object-position:center;">` : ''}
        </div>
      </article>
    `;
  }

  // Full cover panel
  function fullCoverHTML(url, ownerName, sizePx, coverChoice = '1') {
    let colour = '#fff';
    if (coverChoice === '3' || coverChoice === '4') colour = '#633408';
    return `
      <div class="wl-page-full wl-cover" style="position:relative;">
        ${url ? `<img src="${url}" alt="" loading="lazy" decoding="async" style="display:block;width:100%;height:100%;object-fit:cover;">` : ''}
        ${ownerName ? `
          <div class="wl-cover__name" style="
                 position:absolute;left:50%;top:6%;transform:translateX(-50%);
                 font-family:'Book Antiqua','Palatino Linotype',Palatino,serif;
                 color:${colour}; font-weight:800;
                 font-size:${sizePx || 56}px; text-shadow:0 3px 8px rgba(0,0,0,.45),0 0 2px rgba(0,0,0,.25);
                 white-space:nowrap;max-width:90%;overflow:hidden;text-overflow:ellipsis;
                 pointer-events:none;text-align:center;">
            ${ownerName}
          </div>` : ''}
      </div>
    `;
  }

  function emptyPocketHTML() {
    return `
      <article class="wl-card wl-pocket-empty" data-drop-slot="1" style="min-height:1px;">
        <div class="wl-card__image-link" aria-hidden="true"></div>
        <div class="wl-empty-label">Empty pocket</div>
      </article>
    `;
  }

  /* ===================== Personalisation (covers + name) ===================== */
  const LS_COVER       = 'wl_cover_choice';            // '1'|'2'|'3'|'4'
  const LS_NAME        = 'wl_owner_name';              // free text
  const LS_USE_ACCOUNT = 'wl_use_account_name';        // 'true'|'false'

  function getCoverConfig(section) {
    const urls = {
      1: section.getAttribute('data-cover_1') || '',
      2: section.getAttribute('data-cover_2') || '',
      3: section.getAttribute('data-cover_3') || '',
      4: section.getAttribute('data-cover_4') || ''
    };
    const defaultChoice = section.getAttribute('data-default_cover') || '1';
    const backCover = section.getAttribute('data-backcover_img') || '';
    const accountName = section.getAttribute('data-account_name') || '';

    const chosen = localStorage.getItem(LS_COVER) || defaultChoice;
    const useAccount = localStorage.getItem(LS_USE_ACCOUNT) === 'true';
    const savedName = localStorage.getItem(LS_NAME) || '';

    const ownerName = useAccount ? (accountName || savedName) : savedName;

    return { urls, chosen, backCover, accountName, ownerName, useAccount };
  }

  function wirePersonaliseUI(section, rerender) {
    const { accountName, useAccount } = getCoverConfig(section);

    section.querySelectorAll('.wl-coverpick').forEach(btn => {
      const choice = btn.getAttribute('data-cover-choice');
      if (choice === (localStorage.getItem(LS_COVER) || section.getAttribute('data-default_cover') || '1')) {
        btn.classList.add('is-active');
      }
      btn.addEventListener('click', () => {
        section.querySelectorAll('.wl-coverpick').forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        try { localStorage.setItem(LS_COVER, String(choice)); } catch {}
        rerender();
      });
    });

    const chk = section.querySelector('.wl-name__use-account');
    const input = section.querySelector('.wl-name__input');

    if (chk) {
      chk.checked = useAccount;
      chk.disabled = !accountName;
      chk.addEventListener('change', () => {
        try { localStorage.setItem(LS_USE_ACCOUNT, chk.checked ? 'true' : 'false'); } catch {}
        if (chk.checked && accountName) {
          if (input) {
            input.value = accountName;
            input.disabled = true;
          }
          try { localStorage.setItem(LS_NAME, ''); } catch {}
        } else {
          if (input) {
            input.disabled = false;
            try { localStorage.setItem(LS_NAME, input.value.trim()); } catch {}
          }
        }
        rerender();
      });
    }

    if (input) {
      const stored = localStorage.getItem(LS_NAME) || '';
      if (useAccount && accountName) {
        input.value = accountName;
        input.disabled = true;
      } else {
        input.value = stored;
        input.disabled = false;
      }
      input.addEventListener('input', () => {
        if (!chk || !chk.checked) {
          try { localStorage.setItem(LS_NAME, input.value.trim()); } catch {}
          rerender();
        }
      });
    }
  }

  /* ====================== Paging / spread sequencing (binder) ====================== */
  function toPages(items) {
    const pages = [];
    for (let i = 0; i < items.length; i += ITEMS_PER_PAGE) {
      pages.push(items.slice(i, i + ITEMS_PER_PAGE));
    }
    return pages.length ? pages : [[]];
  }

  function computeSpread(pages, spreadIndex, hasBackCover) {
    if (spreadIndex === 0) {
      return { left: { mode: 'frontCover' }, right: { mode: 'empty' } };
    }
    if (spreadIndex === 1) {
      return { left: { mode: 'frontCover' }, right: { mode: 'fronts', cells: pages[0] || [] } };
    }

    const leftBackOf = spreadIndex - 2;
    const rightFrontOf = spreadIndex - 1;

    const left = pages[leftBackOf] ? { mode: 'backs', cells: pages[leftBackOf] } : { mode: 'empty' };
    const rightExists = !!pages[rightFrontOf];

    if (rightExists) {
      return { left, right: { mode: 'fronts', cells: pages[rightFrontOf] } };
    }
    return { left, right: { mode: hasBackCover ? 'backCover' : 'empty' } };
  }

  /* ===================== Fit-to-height (desktop only, binder) ===================== */
  function fitToMaxHeight(section) {
    const viewport = section.querySelector('.wl-binder-viewport');
    const spread   = section.querySelector('.wl-spread');
    if (!viewport || !spread) return;

    const isNarrow = window.matchMedia(MQ_NARROW).matches;
    const cap = Number(section.getAttribute('data-max_h')) || 720;

    spread.style.transform = '';
    spread.style.transformOrigin = '';
    viewport.style.height = '';

    if (isNarrow) return;

    const naturalH = spread.offsetHeight;
    if (!naturalH) return;

    const scale = naturalH > cap ? (cap / naturalH) : 1;
    if (scale < 1) {
      spread.style.transform = `scale(${scale})`;
      spread.style.transformOrigin = 'top center';
      viewport.style.height = `${Math.min(naturalH, cap)}px`;
    }
  }

  /* ============================= Render: LIST VIEW ============================= */
  function injectListStylesOnce() {
    if (document.getElementById('wl-listview-style')) return;
    const s = document.createElement('style');
    s.id = 'wl-listview-style';
    s.textContent = `
      .wl-item{ transition: transform .18s ease, box-shadow .18s ease; }
      .wl-item:hover{ transform: translateY(-2px) scale(1.01); box-shadow: 0 6px 18px rgba(0,0,0,.08); }
      .wl-item .wl-li__img{ transition: transform .18s ease; }
      .wl-item:hover .wl-li__img{ transform: scale(1.02); }
      .wl-item .wl-li__price{ opacity:0; transform: translateY(6px); transition: opacity .18s ease, transform .18s ease; }
      .wl-item:hover .wl-li__price{ opacity:1 !important; transform: translateY(0) !important; }
    `;
    document.head.appendChild(s);
  }

  function ensureListContainer(section) {
    let listWrap = section.querySelector('.wl-listview');
    if (!listWrap) {
      listWrap = document.createElement('div');
      listWrap.className = 'wl-listview';
      listWrap.style.display = 'none';
      listWrap.style.marginTop = '0.5rem';
      listWrap.innerHTML = `
        <div class="wl-listgrid" style="
          display:grid; grid-template-columns:repeat(4,minmax(0,1fr));
          gap:16px;
        "></div>
      `;
      const pageWidth = section.querySelector('.page-width') || section;
      const after = section.querySelector('.wl-pagination') || section.querySelector('.wl-binder-viewport') || pageWidth.lastElementChild;
      after.parentNode.insertBefore(listWrap, after.nextSibling);

      const mq1 = window.matchMedia('(max-width: 990px)');
      const mq2 = window.matchMedia('(max-width: 768px)');
      const mq3 = window.matchMedia('(max-width: 520px)');
      function applyCols() {
        const grid = listWrap.querySelector('.wl-listgrid');
        if (!grid) return;
        if (mq3.matches)       grid.style.gridTemplateColumns = 'repeat(1,minmax(0,1fr))';
        else if (mq2.matches)  grid.style.gridTemplateColumns = 'repeat(2,minmax(0,1fr))';
        else if (mq1.matches)  grid.style.gridTemplateColumns = 'repeat(3,minmax(0,1fr))';
        else                   grid.style.gridTemplateColumns = 'repeat(4,minmax(0,1fr))';
      }
      [mq1, mq2, mq3].forEach(mq => mq.addEventListener?.('change', applyCols));
      applyCols();
    }
    return listWrap;
  }

  function listCardHTML(p, showTitles, showPrices) {
    const img = p.image || p.backImage || '';
    const titleHtml = showTitles
      ? `<h3 style="font-size:.95rem;margin:.5rem 0 .25rem;line-height:1.35">${p.title}</h3>`
      : '';
    const hasPrice = p.price !== null && p.price !== undefined;
    const priceChip = (showPrices && hasPrice)
      ? `<span class="wl-li__price" style="
           display:inline-flex !important; align-items:center; gap:6px;
           padding:.45rem .7rem; border-radius:999px; border:1px solid #ddd;
           font-weight:700; font-size:.95rem; background:#fff; line-height:1;">
             ${formatMoney(p.price)}
         </span>`
      : '';
    const addBtn = p.variantId
      ? `<button type="button" class="wl-card__addtocart" data-variant-id="${p.variantId}" data-handle="${p.handle}" style="
            border:0;border-radius:999px;padding:.55rem .9rem;background:#1a7f37;color:#fff;font-weight:700;cursor:pointer;">Add to Cart</button>`
      : '';
    return `
      <article class="wl-item" data-handle="${p.handle}" style="border:1px solid #eee;border-radius:12px;padding:12px;display:flex;flex-direction:column;gap:10px;background:#fff;">
        <a href="${p.url}" aria-label="${p.title}" style="display:block;overflow:hidden;border-radius:12px;background:#fff;">
          ${img ? `<img class="wl-li__img" src="${img}" alt="${p.title}" loading="lazy" decoding="async" style="display:block;width:100%;height:auto;aspect-ratio:63/88;object-fit:cover;">` : ''}
        </a>
        ${titleHtml}
        <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;justify-content:flex-end;margin-top:auto;">
          ${priceChip}
          ${addBtn}
          <button type="button" class="wl-remove-btn" data-wl-remove data-wl-handle="${p.handle}" style="
            border:1px solid #ddd;background:#fff;border-radius:999px;padding:.5rem .9rem;cursor:pointer;font-weight:700;">Remove</button>
        </div>
      </article>
    `;
  }

  async function renderList(section) {
    injectListStylesOnce();

    // Hide binder UI entirely
    const viewport = section.querySelector('.wl-binder-viewport');
    const pagination = section.querySelector('.wl-pagination');
    const pagerDots = section.querySelector('.wl-pager');
    const personalise = section.querySelector('.wl-personalise');
    if (viewport)   viewport.style.display = 'none';
    if (pagination) pagination.style.display = 'none';
    if (pagerDots)  pagerDots.style.display = 'none';
    if (personalise)personalise.style.display = 'none';

    // Ensure list container exists and show it
    const listWrap = ensureListContainer(section);
    const grid = listWrap.querySelector('.wl-listgrid');
    listWrap.style.display = '';

    // Hydrate items
    const saved = getStore();
    const items = await hydrate(saved);

    // Defaults: show titles & prices unless explicitly disabled
    const showTitlesAttr = section.getAttribute('data-show_titles');
    const showPricesAttr = section.getAttribute('data-show_prices');
    const showTitles = showTitlesAttr === null ? true : (showTitlesAttr === 'true' || showTitlesAttr === '1');
    const showPrices = showPricesAttr === null ? true : !(showPricesAttr === 'false' || showPricesAttr === '0');

    if (!items.length) {
      grid.innerHTML = `<p style="grid-column:1/-1;opacity:.75">Your wishlist is empty.</p>`;
      return;
    }

    grid.innerHTML = items.map(p => listCardHTML(p, showTitles, showPrices)).join('');

    // Wire list interactions (delegated) — bind once
    if (!listWrap.dataset.bound) {
      listWrap.dataset.bound = '1';
      listWrap.addEventListener('click', async (e) => {
        const addBtn = e.target.closest('.wl-card__addtocart');
        if (addBtn) {
          e.preventDefault(); e.stopPropagation();
          const variantId = addBtn.getAttribute('data-variant-id');
          const handle = addBtn.getAttribute('data-handle');
          try {
            await addToCart(variantId, 1);
            const list = getStore();
            const idx = list.findIndex(x => (typeof x === 'string' ? x : x.handle) === handle);
            if (idx >= 0) { list.splice(idx, 1); setStore(list); }
            document.dispatchEvent(new CustomEvent('wishlist:added_to_cart', { detail: { handle, variantId }}));
            document.dispatchEvent(new CustomEvent('wishlist:changed', { detail: { handle, saved: false }}));
          } catch (err) {
            console.error(err);
            alert('Could not add to cart. Please try again.');
          }
          return;
        }

        const rm = e.target.closest('[data-wl-remove]');
        if (rm) {
          e.preventDefault(); e.stopPropagation();
          const handle = rm.getAttribute('data-wl-handle');
          const list = getStore();
          const idx = list.findIndex(x => (typeof x === 'string' ? x : x.handle) === handle);
          if (idx >= 0) { list.splice(idx, 1); setStore(list); }
          document.dispatchEvent(new CustomEvent('wishlist:changed', { detail: { handle, saved: false }}));
        }
      }, { passive: false });
    }
  }

  /* ============================= Render: BINDER VIEW ============================= */
  function prefetchImages(urls = []) {
    urls.forEach(u => {
      if (!u) return;
      const i = new Image();
      i.decoding = 'async';
      i.loading = 'eager';
      i.src = u;
    });
  }

  // Enable drag & drop reordering on RIGHT page
  function enableBinderDrag(section) {
    const rightGrid = section.querySelector('.wl-right');
    if (!rightGrid) return;

    if (!document.getElementById('wl-drop-hint-style')) {
      const s = document.createElement('style');
      s.id = 'wl-drop-hint-style';
      s.textContent = `
        .wl-drop-hint{ outline:2px dashed #1a7f37; outline-offset:2px; }
        .wl-card.is-dragging{ opacity:.6; }
      `;
      document.head.appendChild(s);
    }

    const fronts = rightGrid.querySelectorAll('.wl-card.wl-front');
    const empties = rightGrid.querySelectorAll('[data-drop-slot]');
    let dragHandle = null;

    fronts.forEach(card => {
      const handle = card.getAttribute('data-handle');
      if (!handle) return;

      card.addEventListener('dragstart', (e) => {
        dragHandle = handle;
        card.classList.add('is-dragging');
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', handle);
      });

      card.addEventListener('dragend', () => {
        dragHandle = null;
        card.classList.remove('is-dragging');
        rightGrid.querySelectorAll('.wl-drop-hint').forEach(el => el.classList.remove('wl-drop-hint'));
      });

      card.addEventListener('dragover', (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        card.classList.add('wl-drop-hint');
      });
      card.addEventListener('dragleave', () => {
        card.classList.remove('wl-drop-hint');
      });

      card.addEventListener('drop', (e) => {
        e.preventDefault();
        const src = e.dataTransfer.getData('text/plain') || dragHandle;
        const dest = handle;
        if (!src || !dest || src === dest) return;

        const list = getStore();
        const handles = list.map(x => (typeof x === 'string' ? x : x.handle));
        const from = handles.indexOf(src);
        const to   = handles.indexOf(dest);
        if (from < 0 || to < 0) return;

        const moved = list.splice(from, 1)[0];
        list.splice(to, 0, moved);
        setStore(list);

        document.dispatchEvent(new CustomEvent('wishlist:changed'));
      });
    });

    empties.forEach(slot => {
      slot.addEventListener('dragover', (e) => {
        e.preventDefault();
        slot.classList.add('wl-drop-hint');
      });
      slot.addEventListener('dragleave', () => slot.classList.remove('wl-drop-hint'));
      slot.addEventListener('drop', (e) => {
        e.preventDefault();
        const src = e.dataTransfer.getData('text/plain') || dragHandle;
        if (!src) return;

        const list = getStore();
        const handles = list.map(x => (typeof x === 'string' ? x : x.handle));
        const from = handles.indexOf(src);
        if (from < 0) return;

        const visibleFronts = [...rightGrid.querySelectorAll('.wl-card.wl-front')]
          .map(c => c.getAttribute('data-handle')).filter(Boolean);
        const lastHandle = visibleFronts[visibleFronts.length - 1];
        const to = Math.max(handles.indexOf(lastHandle) + 1, 0);

        const moved = list.splice(from, 1)[0];
        list.splice(to, 0, moved);
        setStore(list);

        document.dispatchEvent(new CustomEvent('wishlist:changed'));
      });
    });
  }

  async function renderBinder(section) {
    // Show binder UI, hide list
    const viewport = section.querySelector('.wl-binder-viewport');
    const pagination = section.querySelector('.wl-pagination');
    thePagerDots = section.querySelector('.wl-pager'); // kept var name unique (no const for legacy browsers)
    const personalise = section.querySelector('.wl-personalise');
    const listWrap = section.querySelector('.wl-listview');

    if (listWrap) listWrap.style.display = 'none';
    if (viewport)   viewport.style.display = '';
    if (pagination) pagination.style.display = '';
    if (thePagerDots)  thePagerDots.style.display = '';
    if (personalise)personalise.style.display = '';

    const gridLeft  = section.querySelector('.wl-left');
    const gridRight = section.querySelector('.wl-right');
    const pageEl    = section.querySelector('.wl-page-indicator');
    const pagerEl   = section.querySelector('.wl-pager'); // optional
    if (!gridLeft || !gridRight) return;

    const isNarrow = window.matchMedia(MQ_NARROW).matches;

    const saved = getStore();
    const items = await hydrate(saved);
    const pages = toPages(items);

    const { urls, chosen, backCover, accountName, ownerName: savedOwner, useAccount } = getCoverConfig(section);
    const coverUrl = urls[Number(chosen)] || '';
    const ownerName = (useAccount && accountName) ? accountName : (savedOwner || '');

    const totalSpreads = Math.max(1, 2 + pages.length + (backCover ? 1 : 0));
    const current = Number(section.dataset.currentSpread || 0);
    const clamped = Math.min(current, totalSpreads - 1);
    section.dataset.currentSpread = clamped;

    section.classList.toggle('wl--cover-only', clamped === 0);

    const { left, right } = computeSpread(pages, clamped, !!backCover);

    // LEFT
    if (left.mode === 'frontCover') {
      gridLeft.classList.toggle('wl-page--cover', true);
      gridLeft.innerHTML = fullCoverHTML(coverUrl, ownerName, isNarrow ? 40 : 56, String(Number(chosen)));
    } else if (left.mode === 'backs') {
      gridLeft.classList.toggle('wl-page--cover', false);
      const cells = [...left.cells];
      while (cells.length < PAGE_SIZE_PER_SIDE) cells.push(null);
      gridLeft.innerHTML = cells.map(p => p ? cardBackHTML(p) : emptyPocketHTML()).join('');
    } else {
      gridLeft.classList.toggle('wl-page--cover', false);
      gridLeft.innerHTML = Array.from({ length: PAGE_SIZE_PER_SIDE }).map(() => emptyPocketHTML()).join('');
    }

    // RIGHT
    if (right.mode === 'fronts') {
      gridRight.classList.toggle('wl-page--cover', false);
      const cells = [...right.cells];
      while (cells.length < PAGE_SIZE_PER_SIDE) cells.push(null);
      gridRight.innerHTML = cells.map(p => p ? cardFrontHTML(p) : emptyPocketHTML()).join('');
    } else if (right.mode === 'backCover') {
      gridRight.classList.toggle('wl-page--cover', true);
      gridRight.innerHTML = fullCoverHTML(backCover, '', isNarrow ? 40 : 56);
    } else {
      gridRight.classList.toggle('wl-page--cover', false);
      gridRight.innerHTML = Array.from({ length: PAGE_SIZE_PER_SIDE }).map(() => emptyPocketHTML()).join('');
    }

    // Page indicator + (optional) dots pager
    if (pageEl) {
      pageEl.setAttribute('aria-live', 'polite');
      pageEl.textContent = `Spread ${clamped + 1} of ${totalSpreads}`;
    }
    if (pagerEl) {
      pagerEl.innerHTML = Array.from({ length: totalSpreads })
        .map((_, i) => `<button type="button" class="wl-pager__dot ${i===clamped?'is-active':''}" data-spread="${i}" aria-label="Go to spread ${i+1}"></button>`)
        .join('');
    }

    // Prefetch next spread images for snappier nav
    const nextIdx = clamped + 1;
    const nextFrontPage = pages[nextIdx - 1];
    if (nextFrontPage && nextFrontPage.length) {
      prefetchImages(nextFrontPage.map(p => p && p.image).filter(Boolean));
    }

    // Overlay behaviour
    section.querySelectorAll('.wl-card.wl-front').forEach(card => {
      const overlay = card.querySelector('[data-overlay]');
      if (!overlay) return;
      const show = () => { overlay.style.opacity = '1'; overlay.style.transform = 'translateY(0)'; };
      const hide = () => { overlay.style.opacity = '0'; overlay.style.transform = 'translateY(6px)'; };
      if (isNarrow) { show(); }
      else {
        hide();
        card.addEventListener('mouseenter', show);
        card.addEventListener('mouseleave', hide);
        card.addEventListener('focusin', show);
        card.addEventListener('focusout', hide);
      }
    });

    // Fit to desktop max height
    requestAnimationFrame(() => fitToMaxHeight(section));

    // Re-fit after images load
    section.querySelectorAll('.wl-spread img').forEach(img => {
      if (!img.complete) {
        img.addEventListener('load', () => fitToMaxHeight(section), { once: true, passive: true });
      }
    });

    // Keep fitted if content height changes
    if (!section._wlRO) {
      const spread = section.querySelector('.wl-spread');
      if (spread && 'ResizeObserver' in window) {
        section._wlRO = new ResizeObserver(() => fitToMaxHeight(section));
        section._wlRO.observe(spread);
      }
    }

    // Enable drag & drop reordering on the right page
    enableBinderDrag(section);
  }

  /* ============================ Controls & wiring ============================ */
  function setViewMode(section, mode) {
    try { localStorage.setItem(LS_VIEW_MODE, mode); } catch {}
    const viewbar = section.querySelector('.wl-viewbar');
    if (viewbar) {
      viewbar.querySelectorAll('.wl-viewbtn').forEach(b => {
        const v = b.getAttribute('data-view');
        b.setAttribute('aria-pressed', String(v === mode));
      });
    }
  }

  async function renderByMode(section) {
    const mode = (localStorage.getItem(LS_VIEW_MODE) || VIEW_BINDER);
    if (mode === VIEW_LIST) await renderList(section);
    else await renderBinder(section);
  }

  function bindBinderControls(section) {
    // View toggle (Binder/List)
    section.querySelector('.wl-viewbar')?.addEventListener('click', (e) => {
      const btn = e.target.closest('.wl-viewbtn');
      if (!btn) return;
      const mode = btn.getAttribute('data-view') === VIEW_LIST ? VIEW_LIST : VIEW_BINDER;
      setViewMode(section, mode);
      renderByMode(section);
    });

    // Prev / Next (binder only)
    section.querySelectorAll('.wl-prev').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        if ((localStorage.getItem(LS_VIEW_MODE) || VIEW_BINDER) !== VIEW_BINDER) return;
        section.dataset.currentSpread = Math.max(0, (Number(section.dataset.currentSpread || 0) - 1));
        renderBinder(section);
      });
    });
    section.querySelectorAll('.wl-next').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        if ((localStorage.getItem(LS_VIEW_MODE) || VIEW_BINDER) !== VIEW_BINDER) return;
        section.dataset.currentSpread = Number(section.dataset.currentSpread || 0) + 1;
        renderBinder(section);
      });
    });

    // Keyboard nav (binder only)
    section.addEventListener('keydown', (e) => {
      const mode = (localStorage.getItem(LS_VIEW_MODE) || VIEW_BINDER);
      if (mode !== VIEW_BINDER) return;
      if (e.key === 'ArrowLeft') {
        section.dataset.currentSpread = Math.max(0, (Number(section.dataset.currentSpread || 0) - 1));
        renderBinder(section);
      } else if (e.key === 'ArrowRight') {
        section.dataset.currentSpread = Number(section.dataset.currentSpread || 0) + 1;
        renderBinder(section);
      } else if (e.key === 'Home') {
        section.dataset.currentSpread = 0;
        renderBinder(section);
      } else if (e.key === 'End') {
        section.dataset.currentSpread = 9999;
        renderBinder(section);
      }
    });

    // Swipe nav (binder only, mobile)
    let touchStartX = 0, touchStartY = 0, swiping = false;
    const SWIPE_THRESHOLD = 40;

    section.addEventListener('touchstart', (e) => {
      const mode = (localStorage.getItem(LS_VIEW_MODE) || VIEW_BINDER);
      if (mode !== VIEW_BINDER) return;
      if (!e.touches || e.touches.length !== 1) return;
      const t = e.touches[0];
      touchStartX = t.clientX; touchStartY = t.clientY; swiping = true;
    }, { passive: true });

    section.addEventListener('touchmove', (e) => {
      const mode = (localStorage.getItem(LS_VIEW_MODE) || VIEW_BINDER);
      if (mode !== VIEW_BINDER) return;
      if (!swiping || !e.touches || e.touches.length !== 1) return;
      const dx = e.touches[0].clientX - touchStartX;
      const dy = e.touches[0].clientY - touchStartY;
      if (Math.abs(dy) > Math.abs(dx)) swiping = false;
    }, { passive: true });

    section.addEventListener('touchend', (e) => {
      const mode = (localStorage.getItem(LS_VIEW_MODE) || VIEW_BINDER);
      if (mode !== VIEW_BINDER) return;
      if (!swiping) return;
      const t = e.changedTouches && e.changedTouches[0];
      if (!t) return;
      const dx = t.clientX - touchStartX;
      if (dx <= -SWIPE_THRESHOLD) {
        section.dataset.currentSpread = Number(section.dataset.currentSpread || 0) + 1;
        renderBinder(section);
      } else if (dx >= SWIPE_THRESHOLD) {
        section.dataset.currentSpread = Math.max(0, (Number(section.dataset.currentSpread || 0) - 1));
        renderBinder(section);
      }
      swiping = false;
    }, { passive: true });

    // Dots pager (binder only)
    section.querySelector('.wl-pager')?.addEventListener('click', (e) => {
      const mode = (localStorage.getItem(LS_VIEW_MODE) || VIEW_BINDER);
      if (mode !== VIEW_BINDER) return;
      const dot = e.target.closest('.wl-pager__dot');
      if (!dot) return;
      section.dataset.currentSpread = Number(dot.getAttribute('data-spread')) || 0;
      renderBinder(section);
    });

    // Card-level interactions (binder view)
    section.addEventListener('click', async (e) => {
      const mode = (localStorage.getItem(LS_VIEW_MODE) || VIEW_BINDER);
      if (mode !== VIEW_BINDER) return;

      const removeBtn = e.target.closest('.wl-card__remove');
      if (removeBtn) return;

      const addBtn = e.target.closest('.wl-card__addtocart');
      if (addBtn) {
        e.preventDefault(); e.stopPropagation();
        const variantId = addBtn.getAttribute('data-variant-id');
        const handle = addBtn.getAttribute('data-handle');
        try {
          await addToCart(variantId, 1);
          const list = getStore();
          const idx = list.findIndex(x => (typeof x === 'string' ? x : x.handle) === handle);
          if (idx >= 0) { list.splice(idx, 1); setStore(list); }
          document.dispatchEvent(new CustomEvent('wishlist:added_to_cart', { detail: { handle, variantId }}));
          document.dispatchEvent(new CustomEvent('wishlist:changed', { detail: { handle, saved: false }}));
          renderBinder(section);
        } catch (err) {
          console.error(err);
          alert('Could not add to cart. Please try again.');
        }
        return;
      }

      const card = e.target.closest('.wl-card.wl-front');
      if (!card) return;
      const a = card.querySelector('a.wl-card__image-link');
      if (e.target.closest('[data-overlay]')) { e.preventDefault(); return; }
      if (a && a.href) {
        if (e.metaKey || e.ctrlKey) window.open(a.href, '_blank');
        else window.location.href = a.href;
      }
    });

    // Delegated remove (applies for binder view)
    section.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-wl-remove]');
      if (!btn) return;
      e.preventDefault(); e.stopPropagation();
      const handle = btn.getAttribute('data-wl-handle');
      if (!handle) return;
      const list = getStore();
      const idx = list.findIndex(x => (typeof x === 'string' ? x : x.handle) === handle);
      if (idx >= 0) { list.splice(idx, 1); setStore(list); }
      document.dispatchEvent(new CustomEvent('wishlist:changed', { detail: { handle, saved: false } }));
    });
  }

  function bindAndRenderBinders(scope = document) {
    scope.querySelectorAll('.wl-binder').forEach((section) => {
      if (!section.dataset.wlBound) {
        section.dataset.wlBound = '1';
        section.dataset.currentSpread = section.dataset.currentSpread || 0;

        if (!section.getAttribute('data-max_h')) {
          section.setAttribute('data-max_h', '720');
        }

        section.setAttribute('tabindex', '0'); // keyboard nav
        bindBinderControls(section);

        // Personalise controls (COVER + NAME)
        wirePersonaliseUI(section, () => renderByMode(section));

        // Initialise view toggle buttons to saved state
        const savedMode = (localStorage.getItem(LS_VIEW_MODE) || VIEW_BINDER);
        const viewbar = section.querySelector('.wl-viewbar');
        if (viewbar) {
          viewbar.querySelectorAll('.wl-viewbtn').forEach(b => {
            const v = b.getAttribute('data-view');
            b.setAttribute('aria-pressed', String(v === savedMode));
          });
        }
      }
      renderByMode(section);
    });
  }

  /* ============================ Boot & global ============================ */
  document.addEventListener('DOMContentLoaded', () => {
    bindAndRenderBinders();

    // Throttled resize: only relevant for binder fit
    let rAF;
    window.addEventListener('resize', () => {
      cancelAnimationFrame(rAF);
      rAF = requestAnimationFrame(() => {
        document.querySelectorAll('.wl-binder').forEach((section) => {
          const mode = (localStorage.getItem(LS_VIEW_MODE) || VIEW_BINDER);
          if (mode === VIEW_BINDER) fitToMaxHeight(section);
        });
      });
    }, { passive: true });
  });

  document.addEventListener('shopify:section:load', (evt) => bindAndRenderBinders(evt.target));

  // Batch rerenders for rapid wishlist changes
  let renderQueued = false;
  function rerenderAllBinders() {
    if (renderQueued) return;
    renderQueued = true;
    requestAnimationFrame(() => {
      document.querySelectorAll('.wl-binder').forEach(renderByMode);
      renderQueued = false;
    });
  }

  document.addEventListener('wishlist:changed', () => {
    rerenderAllBinders();
    initButtons();
  });
})();
